import React from "react";
import { Button, Form, Offcanvas } from "react-bootstrap";
import { useOutletContext } from "react-router-dom";
import AccountStatusBadge from "../features/userManagement/AccountStatusBadge";
import RegistrationAccountOverview from "../features/userManagement/RegistrationAccountOverview";
import RegistrationReviewDocuments from "../features/userManagement/RegistrationReviewDocuments";
import RegistrationSubmissionHistory from "../features/userManagement/RegistrationSubmissionHistory";
import LocalizedTextField from "../features/userManagement/LocalizedTextField";
import RegistrationReviewFilters from "../features/userManagement/RegistrationReviewFilters";
import RegistrationReviewTable from "../features/userManagement/RegistrationReviewTable";
import RegistrationReviewTypeSwitch from "../features/userManagement/RegistrationReviewTypeSwitch";
import SecureDocumentViewer from "../features/userManagement/SecureDocumentViewer";
import useResizableDrawer from "../features/userManagement/useResizableDrawer";
import { getUserManagementText } from "../features/userManagement/translations";
import UserManagementDateTime from "../features/userManagement/UserManagementDateTime";
import UserManagementHeader from "../features/userManagement/UserManagementHeader";
import UserManagementPagination from "../features/userManagement/UserManagementPagination";
import ProtectedProfileImage from "../features/userManagement/ProtectedProfileImage";
import RegistrationReviewsHook from "../hooks/RegistrationReviewsHook";
import "../css/UserManagement.css";
import "../css/RegistrationReviews.css";

const statusLabel = (status, text) =>
  ({
    PENDING_REVIEW: text.pending,
    REUPLOAD_REQUIRED: text.reupload,
    REJECTED: text.rejected,
    APPROVED: text.approved
  })[status] || status || "—";

const REVIEW_OUTCOMES = [
  ["APPROVED", "fa-circle-check", "approve", "approveHint"],
  ["REUPLOAD_REQUIRED", "fa-arrow-rotate-left", "requireReupload", "requireReuploadHint"],
  ["REJECTED", "fa-circle-xmark", "reject", "rejectHint"]
];
const MERCHANT_GROUP_OUTCOMES = REVIEW_OUTCOMES.filter(
  ([decision]) => decision !== "REJECTED"
);

const ReviewOutcomeOptions = ({
  value,
  onChange,
  label,
  text,
  outcomes = REVIEW_OUTCOMES,
  disabled = false
}) => (
  <div
    className={`registration-review-outcomes${outcomes.length === 2 ? " is-two-options" : ""}`}
    role="radiogroup"
    aria-label={label}
  >
    {outcomes.map(([decision, icon, labelKey, hintKey]) => (
      <button
        key={decision}
        type="button"
        role="radio"
        aria-checked={value === decision}
        className={value === decision ? "is-selected" : ""}
        disabled={disabled}
        onClick={() => onChange(decision)}
      >
        <i className={`fa-solid ${icon}`} aria-hidden="true" />
        <span><strong>{text[labelKey]}</strong><small>{text[hintKey]}</small></span>
      </button>
    ))}
  </div>
);

const DocumentGroupDecision = ({
  group,
  reviewStatus,
  decisionForm,
  text,
  onSelectDecision,
  onLanguageChange,
  onReasonChange,
  outcomes,
  disabled = false,
  disabledMessage,
  forcedStatus,
  decisionHint,
  emptyDecisionMessage
}) => {
  const label = text.reviewGroups[group] || group;
  const effectiveStatus = forcedStatus ||
    decisionForm?.decision ||
    reviewStatus ||
    "PENDING_REVIEW";
  return (
    <div
      className={`registration-review-group-decision is-${effectiveStatus.toLowerCase()}`}
      data-review-group={group}
    >
      <header>
        <div>
          <span>{text.reviewDecision}</span>
          <h4>{label}</h4>
        </div>
        <AccountStatusBadge
          status={effectiveStatus}
          label={statusLabel(effectiveStatus, text)}
        />
      </header>
      {!decisionForm ? (
        <p className="registration-review-group-readonly">
          <i className="fa-solid fa-lock" aria-hidden="true" />
          {text.groupNotInCurrentSubmission}
        </p>
      ) : (
        <>
          {decisionHint && (
            <p className="registration-review-group-decision-hint">
              <i className="fa-solid fa-circle-info" aria-hidden="true" />
              {decisionHint}
            </p>
          )}
          {!disabled && !decisionForm.decision && emptyDecisionMessage && (
            <p className="registration-review-group-decision-required">
              <i className="fa-solid fa-triangle-exclamation" aria-hidden="true" />
              {emptyDecisionMessage}
            </p>
          )}
          <ReviewOutcomeOptions
            value={decisionForm.decision}
            onChange={onSelectDecision}
            label={`${text.groupDecision}: ${label}`}
            text={text}
            outcomes={outcomes}
            disabled={disabled}
          />
          {disabled && disabledMessage && (
            <p className="registration-review-group-global-lock">
              <i className="fa-solid fa-lock" aria-hidden="true" />
              {disabledMessage}
            </p>
          )}
          {!disabled && ['REJECTED', 'REUPLOAD_REQUIRED'].includes(
            decisionForm.decision
          ) && (
            <LocalizedTextField
              label={text.reason}
              values={decisionForm.reasonTranslations}
              language={decisionForm.reasonLanguage}
              onLanguageChange={onLanguageChange}
              onChange={onReasonChange}
              placeholder={text.reviewReasonHint}
              required
            />
          )}
        </>
      )}
    </div>
  );
};

const MerchantGlobalRejection = ({ hook, text }) => {
  const rejection = hook.reviewForm.globalRejection || {};
  const selected = Boolean(rejection.selected);
  const wasRejected = Boolean(rejection.wasRejected);
  const reconsidering = Boolean(rejection.reconsidering);
  const title = wasRejected
    ? text.merchantRejectedReviewTitle
    : text.merchantGlobalRejectionTitle;
  const hint = wasRejected
    ? text.merchantRejectedReviewHint
    : text.merchantGlobalRejectionHint;
  const toggleLabel = selected
    ? wasRejected
      ? text.reconsiderMerchantRejection
      : text.cancelMerchantGlobalRejection
    : wasRejected
      ? text.restoreMerchantRejection
      : text.selectMerchantGlobalRejection;
  return (
    <section className={`merchant-global-rejection${selected ? " is-selected" : ""}${wasRejected ? " was-rejected" : ""}${reconsidering ? " is-reconsidering" : ""}`}>
      <div className="merchant-global-rejection-heading">
        <span aria-hidden="true"><i className="fa-solid fa-ban" /></span>
        <div>
          <h4>{title}</h4>
          <p>{hint}</p>
        </div>
      </div>
      <button
        type="button"
        className="merchant-global-rejection-toggle"
        aria-pressed={selected}
        onClick={() => hook.setMerchantGlobalRejection(!selected)}
      >
        <i className={`fa-solid ${selected ? "fa-rotate-left" : "fa-ban"}`} aria-hidden="true" />
        {toggleLabel}
      </button>
      {selected && (
        <>
          <p className="merchant-global-rejection-confirmation">
            <i className="fa-solid fa-circle-info" aria-hidden="true" />
            {wasRejected
              ? text.merchantRejectedReviewSelected
              : text.merchantGlobalRejectionSelected}
          </p>
          <LocalizedTextField
            label={text.reason}
            values={rejection.reasonTranslations || { ar: "", en: "" }}
            language={rejection.reasonLanguage || "en"}
            onLanguageChange={hook.updateMerchantGlobalRejectionLanguage}
            onChange={hook.updateMerchantGlobalRejectionReason}
            placeholder={text.merchantGlobalRejectionReasonHint}
            required
          />
        </>
      )}
      {!selected && reconsidering && (
        <p className="merchant-global-rejection-confirmation is-reconsidering">
          <i className="fa-solid fa-pen-to-square" aria-hidden="true" />
          {text.merchantReconsiderationActive}
        </p>
      )}
    </section>
  );
};

const ReviewNotes = ({ hook, notes = [], text, language }) => (
  <div className="registration-review-internal-notes">
    <div>
      <strong>{text.internalNotesHistory}</strong>
      {notes.length ? (
        <div className="registration-review-comment-list">
          {notes.map((note, index) => (
            <article
              className="registration-review-comment"
              key={note.id || `${note.createdAt}-${index}`}
            >
              <header>
                <strong>
                  {note.author?.name ||
                    note.author?.email ||
                    text.reviewerUnavailable}
                </strong>
                <span>
                  {note.source === "REOPEN_REASON"
                    ? text.reopenReasonSource
                    : text.reviewNoteSource}
                </span>
              </header>
              <p>{note.text}</p>
              <UserManagementDateTime
                value={note.createdAt}
                language={language}
              />
            </article>
          ))}
        </div>
      ) : (
        <p className="registration-review-comment-empty">
          {text.noInternalNotes}
        </p>
      )}
    </div>
    <Form.Group className="registration-review-notes">
      <Form.Label>{text.internalNotes}</Form.Label>
      <Form.Control
        as="textarea"
        rows={3}
        value={hook.reviewForm.notes}
        placeholder={text.internalNotesHint}
        onChange={(event) => hook.updateReviewForm("notes", event.target.value)}
      />
    </Form.Group>
  </div>
);

const RegistrationReviews = () => {
  const outletContext = useOutletContext() || {};
  const language = outletContext.language || "en";
  const direction = outletContext.direction || (language === "ar" ? "rtl" : "ltr");
  const text = getUserManagementText(language);
  const hook = RegistrationReviewsHook(language);
  const {
    drawerBounds,
    drawerResizing,
    drawerStyle,
    drawerWidth,
    cancelDrawerResize,
    moveDrawerResize,
    resetDrawerWidth,
    resizeDrawerWithKeyboard,
    startDrawerResize,
    stopDrawerResize
  } = useResizableDrawer(direction);
  const customer = hook.activeType === "customers";
  const selected = hook.selectedItem;
  const documentMetadata = selected?.documentMetadata || {};
  const submissionHistory = selected?.submissionHistory || [];
  const uploadAttempts = [
    selected?.currentSubmission,
    ...submissionHistory
  ].filter((submission, index, values) =>
    submission?.groups?.length > 0 &&
    values.findIndex((item) => String(item?.id) === String(submission.id)) === index
  );
  const totalPages = hook.pagination?.totalPages || 1;
  const merchantGlobalRejectionSelected = Boolean(
    hook.reviewForm.globalRejection?.selected
  );
  const merchantWasRejected = Boolean(
    hook.reviewForm.globalRejection?.wasRejected
  );
  const renderMerchantGroupDecision = (group, context) => (
    <DocumentGroupDecision
      group={group}
      reviewStatus={context.reviewStatus}
      decisionForm={hook.reviewForm.groupDecisions?.[group]}
      text={text}
      onSelectDecision={(decision) =>
        hook.selectGroupReviewDecision(group, decision)
      }
      onLanguageChange={(value) =>
        hook.updateGroupReviewForm(group, "reasonLanguage", value)
      }
      onReasonChange={(reasonLanguage, value) =>
        hook.updateGroupReviewReason(group, reasonLanguage, value)
      }
      outcomes={MERCHANT_GROUP_OUTCOMES}
      disabled={merchantGlobalRejectionSelected}
      disabledMessage={merchantWasRejected
        ? text.merchantRejectedReviewSelected
        : text.merchantGlobalRejectionSelected}
      emptyDecisionMessage={hook.showDecisionErrors
        ? text.merchantDocumentDecisionRequired
        : ""}
    />
  );
  const renderCustomerDecision = (group, context) => (
    <DocumentGroupDecision
      group={group}
      reviewStatus={context.reviewStatus || selected?.reviewStatus}
      decisionForm={{
        decision: hook.reviewForm.decision,
        reasonTranslations: hook.reviewForm.reasonTranslations,
        reasonLanguage: hook.reviewForm.reasonLanguage
      }}
      text={text}
      onSelectDecision={hook.selectReviewDecision}
      onLanguageChange={(value) => hook.updateReviewForm("reasonLanguage", value)}
      onReasonChange={hook.updateReviewReason}
    />
  );

  return (
    <>
      <main className="user-management-page registration-reviews-page" dir={direction}>
        <RegistrationReviewTypeSwitch
          activeType={hook.activeType}
          onChange={hook.changeType}
          text={text}
        />
        <UserManagementHeader
          eyebrow={text.navigation.parent}
          title={text.registrationReviews}
          subtitle={text.registrationReviewQueueHint}
          total={hook.pagination?.totalResults || 0}
          totalLabel={text.total}
          refreshLabel={text.refresh}
          onRefresh={hook.refresh}
          refreshing={hook.refreshing || hook.loading}
        />

        <section className="account-list-card registration-review-list-card">
          <RegistrationReviewFilters
            type={hook.activeType}
            filters={hook.filters}
            searchDraft={hook.searchDraft}
            onSearchChange={hook.setSearchDraft}
            onChange={hook.updateFilters}
            onClear={hook.clearFilters}
            text={text}
          />
          <RegistrationReviewTable
            type={hook.activeType}
            items={hook.items}
            loading={hook.initialLoading ?? hook.loading}
            onReview={hook.openDetails}
            text={text}
            language={language}
            filtered={hook.hasActiveFilters}
          />
          <UserManagementPagination
            page={hook.pagination?.currentPage || hook.currentPage}
            totalPages={totalPages}
            perPage={hook.filters.perPage}
            onPageChange={hook.setCurrentPage}
            onPerPageChange={(perPage) => hook.updateFilters({ perPage, page: 1 })}
            rowsPerPageLabel={text.rowsPerPage}
            pageLabel={text.page}
            previousLabel={text.previous}
            nextLabel={text.next}
            direction={direction}
          />
        </section>
      </main>

      <Offcanvas
        show={hook.showDetails}
        onHide={() => {
          cancelDrawerResize();
          hook.closeDetails();
        }}
        placement={direction === "rtl" ? "start" : "end"}
        className="registration-review-drawer account-drawer resizable-details-drawer"
        style={drawerStyle}
      >
        <div
          className={`account-drawer-resizer${drawerResizing ? " is-resizing" : ""}`}
          role="separator"
          aria-orientation="vertical"
          aria-label={text.resizeRegistrationReviewDrawer}
          aria-valuemin={drawerBounds.min}
          aria-valuemax={drawerBounds.max}
          aria-valuenow={drawerWidth}
          tabIndex="0"
          title={text.resizeRegistrationReviewDrawer}
          onPointerDown={startDrawerResize}
          onPointerMove={moveDrawerResize}
          onPointerUp={stopDrawerResize}
          onPointerCancel={stopDrawerResize}
          onLostPointerCapture={stopDrawerResize}
          onDoubleClick={resetDrawerWidth}
          onKeyDown={resizeDrawerWithKeyboard}
        >
          <span aria-hidden="true" />
        </div>
        <Offcanvas.Header closeButton>
          <div>
            <p className="registration-review-drawer-eyebrow">
              {customer ? text.customerRegistration : text.merchantRegistration}
            </p>
            <Offcanvas.Title>{text.registrationReviewDetails}</Offcanvas.Title>
          </div>
        </Offcanvas.Header>
        <Offcanvas.Body dir={direction}>
          {hook.detailLoading || !selected ? (
            <div className="registration-review-drawer-loading">
              <span className="spinner-border spinner-border-sm" aria-hidden="true" />
              {text.loading}
            </div>
          ) : (
            <>
              <section className="registration-review-summary">
                <button
                  type="button"
                  className="registration-review-avatar"
                  disabled={!(selected.profileImageAvailable || selected.profileImage) || hook.documentLoading === "profileImage"}
                  aria-label={text.openProfileImage}
                  onClick={() => hook.openDocument("profileImage", text.profileImage)}
                >
                  <ProtectedProfileImage
                    type={hook.activeType}
                    accountId={selected.id || selected._id}
                    available={Boolean(selected.profileImageAvailable || selected.profileImage)}
                    name={selected.fullName || selected.name}
                    imageLabel={text.profileImage}
                    className="registration-review-avatar-image"
                  />
                </button>
                <div>
                  <h2>
                    {customer
                      ? selected.fullName || selected.name
                      : selected.nameAr || selected.name || selected.nameEn}
                  </h2>
                  <p>{selected.email}</p>
                </div>
                <AccountStatusBadge
                  status={selected.reviewStatus}
                  label={statusLabel(selected.reviewStatus, text)}
                />
              </section>

              <section className="registration-review-section">
                <div className="registration-review-section-title">
                  <span><i className="fa-regular fa-user" aria-hidden="true" /></span>
                  <div><h3>{text.overview}</h3><p>{text.registrationOverviewHint}</p></div>
                </div>
                <RegistrationAccountOverview
                  type={hook.activeType}
                  account={selected}
                  text={text}
                  language={language}
                  revealedNationalId={hook.revealedNationalId}
                  nationalIdLoading={hook.nationalIdLoading}
                  onToggleNationalId={hook.toggleNationalId}
                />
              </section>

              <section className="registration-review-section">
                <div className="registration-review-section-title">
                  <span><i className="fa-regular fa-folder-open" aria-hidden="true" /></span>
                  <div><h3>{text.registrationDocuments}</h3><p>{text.protectedDocumentsHint}</p></div>
                </div>
                <RegistrationReviewDocuments
                  type={hook.activeType}
                  submission={selected.currentSubmission}
                  legacyMetadata={documentMetadata}
                  onViewGallery={hook.openDocumentGallery}
                  renderGroupFooter={customer
                    ? renderCustomerDecision
                    : renderMerchantGroupDecision}
                  loading={hook.documentLoading}
                  text={text}
                />
                {!customer && (
                  <MerchantGlobalRejection hook={hook} text={text} />
                )}
              </section>

              {uploadAttempts.length > 0 && (
                <section className="registration-review-section">
                  <div className="registration-review-section-title">
                    <span><i className="fa-solid fa-clock-rotate-left" aria-hidden="true" /></span>
                    <div>
                      <h3>{text.previousUploadAttempts}</h3>
                      <p>{text.previousUploadAttemptsHint}</p>
                    </div>
                  </div>
                  <RegistrationSubmissionHistory
                    submissions={uploadAttempts}
                    currentSubmissionId={selected.currentSubmission?.id}
                    language={language}
                    text={text}
                    loading={hook.documentLoading}
                    onViewGallery={hook.openDocumentGallery}
                  />
                </section>
              )}

              <section className="registration-review-section registration-review-notes-section">
                <ReviewNotes
                  hook={hook}
                  notes={selected.internalNotes || []}
                  text={text}
                  language={language}
                />
              </section>

              <footer className="registration-review-actions">
                <Button variant="outline-secondary" onClick={hook.closeDetails}>
                  {text.cancel}
                </Button>
                <Button
                  variant="primary"
                  disabled={Boolean(hook.actionLoading)}
                  onClick={hook.submitReview}
                >
                  {hook.actionLoading && <span className="spinner-border spinner-border-sm" aria-hidden="true" />}
                  {!customer
                    ? merchantGlobalRejectionSelected
                      ? text.rejectRegistration
                      : merchantWasRejected
                        ? text.saveRevisedDecision
                        : text.saveReviewDecisions
                    : ({
                    APPROVED: text.approveRegistration,
                    REUPLOAD_REQUIRED: text.requestReupload,
                    REJECTED: text.rejectRegistration
                  })[hook.reviewForm.decision] || text.saveReviewDecision}
                </Button>
              </footer>
            </>
          )}
        </Offcanvas.Body>
      </Offcanvas>

      <SecureDocumentViewer
        url={hook.documentViewer?.url}
        title={hook.documentViewer?.title}
        index={hook.documentViewer?.index || 0}
        total={hook.documentViewer?.items?.length || 1}
        onPrevious={() => hook.navigateDocumentViewer(-1)}
        onNext={() => hook.navigateDocumentViewer(1)}
        onClose={hook.closeDocumentViewer}
        loading={Boolean(hook.documentLoading)}
        text={text}
      />
    </>
  );
};

export default RegistrationReviews;
